import { useState, useRef, useEffect } from 'react';
import { useOutsideAlerter } from '../../useOutsideAlerter';
import CreateField from './CreateField';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import useAnalytics from '../../analytics/analytics';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Field, Label, Description } from '@headlessui/react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useUser } from '../../user/UserContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { VisitorTrackNode } from '../tabs/types';
import { 
    ChevronDown,
    ChevronRight,
    GripVertical,
    Pencil,
    FileText,
    Mail,
    Phone,
    MoreHorizontal,
    Trash2,
    Database,
    MessageCircleQuestion,
    Sparkles,
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface FormField {
    label: string;
    type: 'text' | 'email' | 'tel' | 'number' | 'select' | 'checkbox' | 'radio' | 'textarea' | 'dropdown' | 'multiselect' | 'url' | 'date';
    required: boolean;
    name: string;
    options?: string[];
    hidden?: boolean;
    question?: boolean;
    ai_label?: boolean;
    description?: string;
    requireCountryCode?: boolean;
}

interface FormProps {
    form: FormField[];
    setForm: (form: FormField[]) => void;
    setNeedsUpdate: (value: boolean) => void;
    agId?: string;
    agentName: string;
    variant?: 'standard' | 'qualifying';
    /** Workflow track nodes (used to prevent deleting referenced fields). */
    trackNodes?: VisitorTrackNode[];
    /** Setter for workflow track nodes (used for auto-cleaning references on field delete). */
    setTrackNodes?: (value: VisitorTrackNode[]) => void;
    /** Agent tools/actions config (CRM + scheduling mappings). */
    actions?: Record<string, any>;
    /** Setter for actions/tools config (used for auto-cleaning references on field delete). */
    setActions?: (value: Record<string, any>) => void;
    qualifyVisitor?: boolean;
    setQualifyVisitor?: (value: boolean) => void;
    workEmailOnly?: boolean;
    setWorkEmailOnly?: (value: boolean) => void;
    requireContactInfo?: boolean;
    setRequireContactInfo?: (value: boolean) => void;
    messagesBeforeContactInfo?: number;
    setMessagesBeforeContactInfo?: (value: number) => void;
    hideContactUsButton?: boolean;
    setHideContactUsButton?: (value: boolean) => void;
}

const Form = ({
    form,
    setForm,
    setNeedsUpdate,
    agId,
    agentName,
    variant = 'standard',
    trackNodes,
    setTrackNodes,
    actions,
    setActions,
    qualifyVisitor,
    setQualifyVisitor,
    workEmailOnly,
    setWorkEmailOnly,
    requireContactInfo,
    setRequireContactInfo,
    messagesBeforeContactInfo,
    setMessagesBeforeContactInfo,
    hideContactUsButton,
    setHideContactUsButton
}: FormProps) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [activeField, setActiveField] = useState<FormField | null>(null);
    const [creatingQuestion, setCreatingQuestion] = useState(false);
    const [creatingAiLabel, setCreatingAiLabel] = useState(false);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        settings: true
    });
    const { captureEvent } = useAnalytics();
    const { checkLicenseType } = useUser();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require 8px of movement before drag starts
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const hasEmailField = form.some((f) => f.name === 'email');
    const hasPhoneField = form.some((f) => f.name === 'phone');

    const isStandard = variant === 'standard';

    const toggleSection = (section: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const enforceContactRequired = (incomingForm: FormField[]) => {
        const nextForm = [...incomingForm];
        const emailIdx = nextForm.findIndex(f => f.name === 'email');
        const phoneIdx = nextForm.findIndex(f => f.name === 'phone');
        const emailPresent = emailIdx !== -1;
        const phonePresent = phoneIdx !== -1;

        if (emailPresent && !phonePresent) {
            if (!nextForm[emailIdx].required) {
                nextForm[emailIdx] = { ...nextForm[emailIdx], required: true };
            }
        } else if (!emailPresent && phonePresent) {
            if (!nextForm[phoneIdx].required) {
                nextForm[phoneIdx] = { ...nextForm[phoneIdx], required: true };
            }
        } else if (emailPresent && phonePresent) {
            const emailReq = !!nextForm[emailIdx].required;
            const phoneReq = !!nextForm[phoneIdx].required;
            if (!emailReq && !phoneReq) {
                nextForm[emailIdx] = { ...nextForm[emailIdx], required: true };
            }
        }
        return nextForm;
    };

    // Ensure invariant: at least one of Email/Phone is required at all times
    useEffect(() => {
        if (!isStandard) return;
        const emailIdx = form.findIndex(f => f.name === 'email');
        const phoneIdx = form.findIndex(f => f.name === 'phone');
        const emailPresent = emailIdx !== -1;
        const phonePresent = phoneIdx !== -1;
        if (!emailPresent && !phonePresent) return;

        const emailReq = emailPresent ? !!form[emailIdx].required : false;
        const phoneReq = phonePresent ? !!form[phoneIdx].required : false;
        if ((emailPresent && !phonePresent && !emailReq) ||
            (!emailPresent && phonePresent && !phoneReq) ||
            (emailPresent && phonePresent && !emailReq && !phoneReq)) {
            const fixed = enforceContactRequired(form);
            if (JSON.stringify(fixed) !== JSON.stringify(form)) {
                setForm(fixed);
                setNeedsUpdate(true);
            }
        }
    }, [form, isStandard]);

    const addSystemField = (name: 'email' | 'phone') => {
        if (!isStandard) return;
        if (name === 'email') {
            const isOnly = !hasPhoneField;
            const newField: FormField = { name: 'email', label: 'Email', type: 'email', required: isOnly ? true : false };
            const next = enforceContactRequired([...form, newField]);
            setForm(next);
        } else {
            const isOnly = !hasEmailField;
            const newField: FormField = { name: 'phone', label: 'Phone', type: 'tel', required: isOnly ? true : false };
            const next = enforceContactRequired([...form, newField]);
            setForm(next);
        }
        setNeedsUpdate(true);
    };

    const handleToggleEmail = (checked: boolean) => {
        if (!isStandard) return;
        if (checked) {
            if (!hasEmailField) addSystemField('email');
            return;
        }
        // Prevent removing both email and phone
        if (!hasPhoneField) {
            toast.error('At least one of Email or Phone must be present', toastConfig);
            return;
        }
        const remaining = form.filter((f) => f.name !== 'email');
        const next = enforceContactRequired(remaining);
        setForm(next);
        setNeedsUpdate(true);
    };

    const handleTogglePhone = (checked: boolean) => {
        if (!isStandard) return;
        if (checked) {
            if (!hasPhoneField) addSystemField('phone');
            return;
        }
        // Prevent removing both email and phone
        if (!hasEmailField) {
            toast.error('At least one of Email or Phone must be present', toastConfig);
            return;
        }
        const remaining = form.filter((f) => f.name !== 'phone');
        const next = enforceContactRequired(remaining);
        setForm(next);
        setNeedsUpdate(true);
    };

    const handleDragEnd = (event: any) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = form.findIndex((item) => item.name === active.id);
            const newIndex = form.findIndex((item) => item.name === over.id);

            let newForm = arrayMove(form, oldIndex, newIndex);
            setForm(newForm);
            setNeedsUpdate(true);
        }
    };

    const toggleModal = (field?: FormField) => {
        if (modalVisible) {
            setActiveField(null);
            setCreatingQuestion(false);
            setCreatingAiLabel(false);
        } else {
            setActiveField(field || null);
            setCreatingQuestion(!!field?.question);
            setCreatingAiLabel(!!field?.ai_label);
        }
        setModalVisible(!modalVisible);
    };

    const addField = (field: FormField) => {
        if (field.label === "") {
            toast.error('Field name is required', toastConfig);
            return false;
        }

        if ((field.type === 'select' || field.type === 'radio' || field.type === 'dropdown' || field.type === 'multiselect') && (!field.options || field.options.length === 0)) {
            toast.error('Select/Dropdown/Multi-select fields must have at least one option', toastConfig);
            return false;
        }

        const isQuestionField = !!(field as any).question;
        const isAiLabelField = !!(field as any).ai_label;
        // System fields that should keep their specific names
        const systemFieldNames = ['email', 'first_name', 'last_name', 'phone', 'company', 'job_title'];
        const isSystemField = field.name && systemFieldNames.includes(field.name);

        const newField: FormField = {
            ...field,
            name: isSystemField
                ? field.name  // Keep system field names
                : isAiLabelField
                    ? (() => {
                        // Use label_1, label_2, etc. for AI labels
                        const maxNum = form.reduce((max, f) => {
                            const match = f.name.match(/^label_(\d+)$/);
                            return match ? Math.max(max, parseInt(match[1], 10)) : max;
                        }, 0);
                        return `label_${maxNum + 1}`;
                    })()
                    : (() => {
                        // Use numeric IDs for all custom fields (both questions and regular fields)
                        const maxNum = form.reduce((max, f) => {
                            const n = parseInt(f.name, 10);
                            return isNaN(n) ? max : Math.max(max, n);
                        }, 0);
                        return String(maxNum + 1);
                    })(),
        };

        if (!isQuestionField && form.some((f) => f.name === newField.name)) {
            toast.error('Field with this name already exists', toastConfig);
            return false;
        }

        // For non-question fields, ensure at least one of email or phone remains in the form
        const proposedForm = [...form, newField];
        if (!isQuestionField) {
            const nextHasEmail = proposedForm.some(f => f.name === 'email');
            const nextHasPhone = proposedForm.some(f => f.name === 'phone');
            if (!nextHasEmail && !nextHasPhone) {
                toast.error('At least one of Email or Phone must be present', toastConfig);
                return false;
            }
        }

        setForm(proposedForm);
        setNeedsUpdate(true);
        
        captureEvent('agent_field_added', {
            agent_id: agId,
            agent_name: agentName,
        });
        
        return true;
    };

    const getRemovedValueMappedOptions = (originalField: FormField, updatedField: FormField) => {
        const updatedOptions = updatedField.options || [];
        const removedOptions = (originalField.options || []).filter((option) => !updatedOptions.includes(option));
        if (removedOptions.length === 0) return [];

        const mappingKeysForField = [originalField.name, `form.${originalField.name}`];
        const crmConfigs: any[] = Array.isArray(actions?.crm) ? actions!.crm : [];
        const warnings: string[] = [];

        const collectAffected = (valueMappings: any, affected: Set<string>) => {
            if (!valueMappings || typeof valueMappings !== 'object' || Array.isArray(valueMappings)) return;
            for (const key of mappingKeysForField) {
                const optionMap = valueMappings[key];
                if (!optionMap || typeof optionMap !== 'object' || Array.isArray(optionMap)) continue;
                removedOptions.forEach((option) => {
                    if (Object.prototype.hasOwnProperty.call(optionMap, option)) {
                        affected.add(option);
                    }
                });
            }
        };

        for (const crm of crmConfigs) {
            if (!crm || typeof crm !== 'object') continue;
            const crmName = String((crm as any).name || 'CRM');
            const crmLabel = crmName === 'hubspot' ? 'HubSpot' : crmName === 'salesforce' ? 'Salesforce' : crmName;

            const affected = new Set<string>();
            collectAffected((crm as any).value_mappings, affected);
            const objectMappings = (crm as any).object_mappings;
            if (objectMappings && typeof objectMappings === 'object' && !Array.isArray(objectMappings)) {
                for (const objectConfig of Object.values(objectMappings)) {
                    if (objectConfig && typeof objectConfig === 'object' && !Array.isArray(objectConfig)) {
                        collectAffected((objectConfig as any).value_mappings, affected);
                    }
                }
            }
            if (affected.size > 0) {
                warnings.push(`${crmLabel}: ${Array.from(affected).map((o) => `"${o}"`).join(', ')}`);
            }
        }

        const scheduling = actions?.scheduling;
        if (scheduling && typeof scheduling === 'object') {
            const affected = new Set<string>();
            collectAffected((scheduling as any).value_mappings, affected);
            if (affected.size > 0) {
                const schedulingName = String((scheduling as any).name || 'scheduler');
                const schedulingLabel = schedulingName === 'revenue-hero'
                    ? 'RevenueHero'
                    : schedulingName === 'chili-piper'
                        ? 'Chili Piper'
                        : schedulingName;
                warnings.push(`${schedulingLabel}: ${Array.from(affected).map((o) => `"${o}"`).join(', ')}`);
            }
        }

        return warnings;
    };

    const editField = (field: FormField) => {
        const originalName = field.name;

        if ((field.type === 'select' || field.type === 'radio' || field.type === 'dropdown' || field.type === 'multiselect') && (!field.options || field.options.length === 0)) {
            toast.error('Select/Dropdown/Multi-select fields must have at least one option', toastConfig);
            return false;
        }

        const systemFieldNames = ['email', 'first_name', 'last_name', 'phone', 'company', 'job_title'];
        const isSystemField = systemFieldNames.includes(originalName);

        const updatedField = {
            ...field,
            name: originalName
        };

        const originalField = form.find((f) => f.name === originalName);
        if (originalField) {
            const valueMappingWarnings = getRemovedValueMappedOptions(originalField, updatedField);
            if (valueMappingWarnings.length > 0) {
                toast.warning(
                    `Some removed or renamed options are value-mapped in your CRM or scheduler integrations (${valueMappingWarnings.join('; ')}). ` +
                    `Update the mapping in Actions so synced values stay correct.`,
                    toastConfig
                );
            }
        }

        let updatedForm = form.map((f) => f.name === originalName ? updatedField : f);
        // If toggling required flags on email/phone, enforce invariant
        if (!(field as any).question && (originalName === 'email' || originalName === 'phone')) {
            updatedForm = enforceContactRequired(updatedForm);
        }
        setForm(updatedForm);
        setNeedsUpdate(true);
        
        captureEvent('agent_field_updated', {
            agent_id: agId,
            agent_name: agentName,
        });

        toggleModal();
        return true;
    };

    const isPlainObject = (value: unknown): value is Record<string, any> =>
        Boolean(value) && typeof value === 'object' && !Array.isArray(value);

    const isValidFormulaForConditionLetters = (formula: string, letters: Set<string>) => {
        const normalized = String(formula || '').trim().toUpperCase();
        if (!normalized || letters.size === 0) return false;

        const tokenRegex = /\s*(AND|OR|NOT|\(|\)|[A-Z])\s*/gy;
        const tokens: string[] = [];
        let cursor = 0;

        while (cursor < normalized.length) {
            tokenRegex.lastIndex = cursor;
            const match = tokenRegex.exec(normalized);
            if (!match) return false;
            tokens.push(match[1]);
            cursor = tokenRegex.lastIndex;
        }

        let expectsOperand = true;
        let depth = 0;
        let hasLetterToken = false;

        for (const token of tokens) {
            const isLetter = /^[A-Z]$/.test(token);

            if (expectsOperand) {
                if (token === 'NOT') {
                    continue;
                }
                if (token === '(') {
                    depth += 1;
                    continue;
                }
                if (isLetter) {
                    if (!letters.has(token)) return false;
                    hasLetterToken = true;
                    expectsOperand = false;
                    continue;
                }
                return false;
            }

            if (token === 'AND' || token === 'OR') {
                expectsOperand = true;
                continue;
            }
            if (token === ')') {
                if (depth === 0) return false;
                depth -= 1;
                continue;
            }

            return false;
        }

        return !expectsOperand && depth === 0 && hasLetterToken;
    };

    const cleanupTrackReferences = (fieldName: string) => {
        const nodes = Array.isArray(trackNodes) ? trackNodes : [];
        let changed = false;
        const conditionFieldKeys = new Set([fieldName, `form.${fieldName}`]);

        let fieldsToCollectRefsRemoved = 0;
        let ruleConditionRefsRemoved = 0;
        let rulesAdjusted = 0;
        let rulesCleared = 0;

        const nextNodes = nodes.map((node) => {
            if (!node || typeof node !== 'object') return node;

            let nodeChanged = false;
            let nextNode = node;

            const originalFields = Array.isArray(node.fields_to_collect) ? node.fields_to_collect : [];
            const filteredFields = originalFields.filter((f) => f !== fieldName);
            const removedCollectRefs = originalFields.length - filteredFields.length;
            if (removedCollectRefs > 0) {
                fieldsToCollectRefsRemoved += removedCollectRefs;
                nextNode = { ...nextNode, fields_to_collect: filteredFields };
                nodeChanged = true;
            }

            const rules = (node as any).classification_rules;
            const originalConditions = Array.isArray(rules?.conditions) ? rules.conditions : [];
            const remainingConditions = originalConditions.filter(
                (condition: any) => !conditionFieldKeys.has(String(condition?.field || ''))
            );
            const removedRuleRefs = originalConditions.length - remainingConditions.length;

            if (removedRuleRefs > 0) {
                ruleConditionRefsRemoved += removedRuleRefs;
                const formula = String(rules?.formula || '');
                const availableLetters = new Set<string>(
                    remainingConditions
                        .map((condition: any) => String(condition?.letter || '').trim().toUpperCase())
                        .filter((letter: string) => Boolean(letter))
                );
                const formulaWasPreserved =
                    remainingConditions.length > 0 &&
                    isValidFormulaForConditionLetters(formula, availableLetters);

                if (formulaWasPreserved) {
                    console.warn('[Form] Preserving routing formula after field-delete cleanup', {
                        removedField: fieldName,
                        trackId: (node as any).id,
                        trackSystemName: (node as any).system_name,
                        formula,
                        availableLetters: Array.from(availableLetters),
                    });
                    nextNode = {
                        ...nextNode,
                        classification_rules: {
                            ...rules,
                            conditions: remainingConditions,
                            formula,
                        },
                    };
                    rulesAdjusted += 1;
                } else {
                    nextNode = { ...nextNode, classification_rules: undefined };
                    rulesCleared += 1;
                }
                nodeChanged = true;
            }

            if (!nodeChanged) return node;
            changed = true;
            return nextNode;
        });

        return {
            changed,
            hadReferences: fieldsToCollectRefsRemoved > 0 || ruleConditionRefsRemoved > 0,
            nextNodes,
            fieldsToCollectRefsRemoved,
            ruleConditionRefsRemoved,
            rulesAdjusted,
            rulesCleared,
        };
    };

    const cleanupActionReferences = (fieldName: string) => {
        const mappingKeysForField = [fieldName, `form.${fieldName}`];
        const currentActions = isPlainObject(actions) ? actions : {};
        let changed = false;
        let mappingsRemoved = 0;

        let nextActions: Record<string, any> = currentActions;

        const scheduling = isPlainObject(currentActions.scheduling) ? currentActions.scheduling : null;
        const schedulingFields = isPlainObject(scheduling?.fields) ? scheduling?.fields : null;
        if (schedulingFields && mappingKeysForField.some((key) => Object.prototype.hasOwnProperty.call(schedulingFields, key))) {
            const nextSchedulingFields = { ...schedulingFields };
            mappingKeysForField.forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(nextSchedulingFields, key)) {
                    delete nextSchedulingFields[key];
                    mappingsRemoved += 1;
                }
            });
            nextActions = { ...nextActions, scheduling: { ...scheduling, fields: nextSchedulingFields } };
            changed = true;
        }

        const crmConfigs = Array.isArray(currentActions.crm) ? currentActions.crm : [];
        if (crmConfigs.length > 0) {
            let crmChanged = false;

            const nextCrm = crmConfigs.map((crmConfig) => {
                if (!isPlainObject(crmConfig)) return crmConfig;

                let configChanged = false;
                let nextConfig = crmConfig as Record<string, any>;

                const legacyFields = isPlainObject(crmConfig.fields) ? crmConfig.fields : null;
                if (legacyFields && mappingKeysForField.some((key) => Object.prototype.hasOwnProperty.call(legacyFields, key))) {
                    const nextLegacyFields = { ...legacyFields };
                    mappingKeysForField.forEach((key) => {
                        if (Object.prototype.hasOwnProperty.call(nextLegacyFields, key)) {
                            delete nextLegacyFields[key];
                            mappingsRemoved += 1;
                        }
                    });
                    nextConfig = { ...nextConfig, fields: nextLegacyFields };
                    configChanged = true;
                }

                const valueMappings = isPlainObject(crmConfig.value_mappings) ? crmConfig.value_mappings : null;
                if (valueMappings && mappingKeysForField.some((key) => Object.prototype.hasOwnProperty.call(valueMappings, key))) {
                    const nextValueMappings = { ...valueMappings };
                    mappingKeysForField.forEach((key) => {
                        delete nextValueMappings[key];
                    });
                    nextConfig = { ...nextConfig, value_mappings: nextValueMappings };
                    configChanged = true;
                }

                const objectMappings = isPlainObject(crmConfig.object_mappings)
                    ? crmConfig.object_mappings
                    : null;
                if (objectMappings) {
                    let objectMappingsChanged = false;
                    const nextObjectMappings: Record<string, any> = { ...objectMappings };

                    for (const [objectKey, objectConfig] of Object.entries(objectMappings)) {
                        if (!isPlainObject(objectConfig)) continue;
                        const objectFields = isPlainObject(objectConfig.fields) ? objectConfig.fields : null;
                        const objectValueMappings = isPlainObject(objectConfig.value_mappings) ? objectConfig.value_mappings : null;
                        const hasFieldRef = Boolean(objectFields) && mappingKeysForField.some((key) => Object.prototype.hasOwnProperty.call(objectFields, key));
                        const hasValueRef = Boolean(objectValueMappings) && mappingKeysForField.some((key) => Object.prototype.hasOwnProperty.call(objectValueMappings, key));
                        if (!hasFieldRef && !hasValueRef) continue;

                        const nextObjectConfig: Record<string, any> = { ...objectConfig };
                        if (hasFieldRef && objectFields) {
                            const nextObjectFields = { ...objectFields };
                            mappingKeysForField.forEach((key) => {
                                if (Object.prototype.hasOwnProperty.call(nextObjectFields, key)) {
                                    delete nextObjectFields[key];
                                    mappingsRemoved += 1;
                                }
                            });
                            nextObjectConfig.fields = nextObjectFields;
                        }
                        if (hasValueRef && objectValueMappings) {
                            const nextObjectValueMappings = { ...objectValueMappings };
                            mappingKeysForField.forEach((key) => {
                                delete nextObjectValueMappings[key];
                            });
                            nextObjectConfig.value_mappings = nextObjectValueMappings;
                        }
                        nextObjectMappings[objectKey] = nextObjectConfig;
                        objectMappingsChanged = true;
                    }

                    if (objectMappingsChanged) {
                        nextConfig = { ...nextConfig, object_mappings: nextObjectMappings };
                        configChanged = true;
                    }
                }

                if (configChanged) {
                    crmChanged = true;
                    return nextConfig;
                }
                return crmConfig;
            });

            if (crmChanged) {
                nextActions = { ...nextActions, crm: nextCrm };
                changed = true;
            }
        }

        return {
            changed,
            hadReferences: mappingsRemoved > 0,
            nextActions,
            mappingsRemoved,
        };
    };

    const getDeleteBlockers = (fieldName: string): string[] => {
        const blockers: string[] = [];
        const mappingKeysForField = [fieldName, `form.${fieldName}`];

        // Workflow references (track fields + routing rules)
        const nodes = Array.isArray(trackNodes) ? trackNodes : [];
        for (const node of nodes) {
            const nodeLabel = (node?.name || '').trim() || node?.system_name || 'Unnamed track';
            const collects = Array.isArray(node?.fields_to_collect) && node.fields_to_collect.includes(fieldName);
            if (collects) {
                blockers.push(`Workflow track "${nodeLabel}" collects it`);
            }

            const conditions = (node as any)?.classification_rules?.conditions;
            if (Array.isArray(conditions)) {
                const usedInRules = conditions.some((c: any) => mappingKeysForField.includes(String(c?.field || '')));
                if (usedInRules) {
                    blockers.push(`Workflow track "${nodeLabel}" uses it in routing rules`);
                }
            }
        }

        // Scheduling mappings (Chili Piper and others)
        const schedulingFields = actions?.scheduling?.fields;
        if (schedulingFields && typeof schedulingFields === 'object' && !Array.isArray(schedulingFields)) {
            if (mappingKeysForField.some((key) => Object.prototype.hasOwnProperty.call(schedulingFields, key))) {
                const schedName = String(actions?.scheduling?.name || 'scheduling');
                blockers.push(`${schedName} field mapping uses it`);
            }
        }

        // CRM mappings (HubSpot + Salesforce + Attio)
        const crmConfigs = actions?.crm;
        if (Array.isArray(crmConfigs)) {
            for (const crm of crmConfigs) {
                if (!crm || typeof crm !== 'object') continue;
                const crmName = String((crm as any).name || '');

                const legacyFields = (crm as any).fields;
                const hasLegacyField = legacyFields && typeof legacyFields === 'object' && !Array.isArray(legacyFields)
                    ? mappingKeysForField.some((key) => Object.prototype.hasOwnProperty.call(legacyFields, key))
                    : false;

                if (crmName === 'hubspot') {
                    if (hasLegacyField) blockers.push('HubSpot field mapping uses it');
                } else if (crmName === 'attio') {
                    if (hasLegacyField) blockers.push('Attio field mapping uses it');
                } else if (crmName === 'salesforce') {
                    if (hasLegacyField) blockers.push('Salesforce Lead mapping uses it');

                    const objectMappings = (crm as any).object_mappings;
                    if (objectMappings && typeof objectMappings === 'object') {
                        for (const objKey of ['lead', 'contact', 'account'] as const) {
                            const obj = (objectMappings as any)[objKey];
                            const objFields = obj?.fields;
                            if (objFields && typeof objFields === 'object' && !Array.isArray(objFields)) {
                                if (mappingKeysForField.some((key) => Object.prototype.hasOwnProperty.call(objFields, key))) {
                                    blockers.push(`Salesforce ${objKey} mapping uses it`);
                                }
                            }
                        }
                    }
                }
            }
        }

        // De-dupe while preserving order
        return Array.from(new Set(blockers));
    };

    const deleteField = (field: FormField) => {
        if (isStandard && (field.name === 'email' || field.name === 'phone')) {
            toast.error('This system field cannot be removed with the trash icon. Use the toggles above.', toastConfig);
            return;
        }

        if (form.length === 1) {
            toast.error('You must have at least one field', toastConfig);
            return;
        }

        const updatedForm = form.filter((f) => f.name !== field.name);
        if (isStandard) {
            const stillHasEmail = updatedForm.some(f => f.name === 'email');
            const stillHasPhone = updatedForm.some(f => f.name === 'phone');
            if (!stillHasEmail && !stillHasPhone) {
                toast.error('At least one of Email or Phone must be present', toastConfig);
                return;
            }
        }

        const trackCleanup = cleanupTrackReferences(field.name);
        const actionCleanup = cleanupActionReferences(field.name);

        const missingSetters: string[] = [];
        if (trackCleanup.hadReferences && !setTrackNodes) {
            missingSetters.push('workflow');
        }
        if (actionCleanup.hadReferences && !setActions) {
            missingSetters.push('integrations');
        }

        if (missingSetters.length > 0) {
            const blockers = getDeleteBlockers(field.name);
            const shown = blockers.slice(0, 3);
            const remainder = blockers.length - shown.length;
            const message =
                `Can't delete "${field.label}" (${field.name}) because it's referenced:\n` +
                shown.map((b) => `- ${b}`).join('\n') +
                (remainder > 0 ? `\n- and ${remainder} more` : '');
            toast.error(message, toastConfig);
            return;
        }

        if (trackCleanup.changed && setTrackNodes) {
            setTrackNodes(trackCleanup.nextNodes);
        }
        if (actionCleanup.changed && setActions) {
            setActions(actionCleanup.nextActions);
        }

        setForm(updatedForm);
        setNeedsUpdate(true);

        const cleanupSummaryParts: string[] = [];
        if (trackCleanup.fieldsToCollectRefsRemoved > 0) {
            cleanupSummaryParts.push(
                `removed ${trackCleanup.fieldsToCollectRefsRemoved} workflow field-collection reference${trackCleanup.fieldsToCollectRefsRemoved === 1 ? '' : 's'}`
            );
        }
        if (trackCleanup.ruleConditionRefsRemoved > 0) {
            cleanupSummaryParts.push(
                `removed ${trackCleanup.ruleConditionRefsRemoved} workflow routing-rule condition${trackCleanup.ruleConditionRefsRemoved === 1 ? '' : 's'}`
            );
        }
        if (trackCleanup.rulesAdjusted > 0) {
            cleanupSummaryParts.push(
                `updated routing rules on ${trackCleanup.rulesAdjusted} track${trackCleanup.rulesAdjusted === 1 ? '' : 's'}`
            );
        }
        if (trackCleanup.rulesCleared > 0) {
            cleanupSummaryParts.push(
                `cleared invalid routing rules on ${trackCleanup.rulesCleared} track${trackCleanup.rulesCleared === 1 ? '' : 's'}`
            );
        }
        if (actionCleanup.mappingsRemoved > 0) {
            cleanupSummaryParts.push(
                `removed ${actionCleanup.mappingsRemoved} integration mapping reference${actionCleanup.mappingsRemoved === 1 ? '' : 's'}`
            );
        }

        if (cleanupSummaryParts.length > 0) {
            toast.info(
                `Deleted "${field.label}" and ${cleanupSummaryParts.join(', ')}.`,
                toastConfig
            );
        }
        
        captureEvent('agent_field_deleted', {
            agent_id: agId,
            agent_name: agentName,
        });
    };

    const modalRef = useRef(null);

    useOutsideAlerter(modalRef, () => toggleModal());

    const getTypeLabel = (type: string) => {
        const typeLabels: Record<string, string> = {
            'text': 'Text',
            'email': 'Email',
            'tel': 'Phone',
            'number': 'Number',
            'url': 'URL',
            'date': 'Date',
            'dropdown': 'Dropdown',
            'checkbox': 'Yes/No',
            'multiselect': 'Multi-select',
            'select': 'Select',
            'radio': 'Radio',
            'textarea': 'Long text'
        };
        return typeLabels[type] || type;
    };

    return (
        <div className="space-y-8">

            {/* Lead Profile Card - Only for standard variant */}
            {isStandard && (
                <section className="space-y-3">
                    <div>
                        <h3 className="text-sm font-semibold">Contact creation</h3>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground">
                            Choose the contact methods that create or identify a visitor.
                        </p>
                    </div>
                    <div className="rounded-lg border bg-card p-4">
                        <div className="grid gap-3 @lg:grid-cols-2">
                            <button
                                type="button"
                                onClick={() => handleToggleEmail(!hasEmailField)}
                                className={cn(
                                    "flex items-center gap-3 rounded-lg border p-3 text-left transition-colors",
                                    hasEmailField
                                        ? "border-foreground/20 bg-muted/20"
                                        : "border-border/70 hover:border-foreground/15"
                                )}
                            >
                                <Mail className="h-4 w-4 text-muted-foreground" />
                                <div className="min-w-0 flex-1">
                                    <span className="text-sm font-medium">Email</span>
                                    <p className="text-xs text-muted-foreground">
                                        {hasEmailField ? 'Enabled' : 'Disabled'}
                                    </p>
                                </div>
                                <span className={cn(
                                    "flex h-4 w-4 items-center justify-center rounded-full border",
                                    hasEmailField ? "border-primary" : "border-muted-foreground/40"
                                )}>
                                    {hasEmailField && <span className="h-2 w-2 rounded-full bg-primary" />}
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => handleTogglePhone(!hasPhoneField)}
                                className={cn(
                                    "flex items-center gap-3 rounded-lg border p-3 text-left transition-colors",
                                    hasPhoneField
                                        ? "border-foreground/20 bg-muted/20"
                                        : "border-border/70 hover:border-foreground/15"
                                )}
                            >
                                <Phone className="h-4 w-4 text-muted-foreground" />
                                <div className="min-w-0 flex-1">
                                    <span className="text-sm font-medium">Phone</span>
                                    <p className="text-xs text-muted-foreground">
                                        {hasPhoneField ? 'Enabled' : 'Disabled'}
                                    </p>
                                </div>
                                <span className={cn(
                                    "flex h-4 w-4 items-center justify-center rounded-full border",
                                    hasPhoneField ? "border-primary" : "border-muted-foreground/40"
                                )}>
                                    {hasPhoneField && <span className="h-2 w-2 rounded-full bg-primary" />}
                                </span>
                            </button>
                        </div>
                        <p className="mt-3 text-xs text-muted-foreground">
                            At least one contact method must remain enabled
                        </p>
                    </div>
                </section>
            )}
            
            {modalVisible && (
                <CreateField
                    addField={addField}
                    editField={editField}
                    setModalVisible={setModalVisible}
                    field={activeField}
                    isQuestion={creatingQuestion || !!activeField?.question}
                    isAiLabel={creatingAiLabel || !!activeField?.ai_label}
                />
            )}

            {/* Fields & Questions Card - Single Sortable List */}
            <section className="space-y-3">
                {/* Header with Add Buttons */}
                <div>
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                            <div className="min-w-0">
                                <h3 className="text-sm font-semibold">Fields, questions &amp; AI labels</h3>
                                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                                    Drag to reorder. Position determines the 'Contact Us' form layout.
                                </p>
                            </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setActiveField(null);
                                    setCreatingQuestion(false);
                                    setCreatingAiLabel(false);
                                    setModalVisible(true);
                                }}
                            >
                                <Database className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                                Add field
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setActiveField(null);
                                    setCreatingQuestion(true);
                                    setCreatingAiLabel(false);
                                    setModalVisible(true);
                                }}
                            >
                                <MessageCircleQuestion className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                                Add question
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setActiveField(null);
                                    setCreatingQuestion(false);
                                    setCreatingAiLabel(true);
                                    setModalVisible(true);
                                }}
                            >
                                <Sparkles className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                                Add AI label
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Single Sortable List */}
                <div className="rounded-lg border bg-card p-3">
                    {form && form.length > 0 ? (
                        <DndContext 
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext 
                                items={form.map(f => f.name)}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="space-y-2">
                                    {form.map((field) => (
                                        <SortableField 
                                            key={field.name}
                                            field={field}
                                            toggleModal={toggleModal}
                                            deleteField={deleteField}
                                            getTypeLabel={getTypeLabel}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    ) : (
                        <div className="text-center py-8 border border-dashed rounded-lg">
                            <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">No fields or questions added yet</p>
                            <div className="flex items-center justify-center gap-2 mt-3">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setActiveField(null);
                                        setCreatingQuestion(false);
                                        setCreatingAiLabel(false);
                                        setModalVisible(true);
                                    }}
                                >
                                    <Database className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                                    Add field
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setActiveField(null);
                                        setCreatingQuestion(true);
                                        setCreatingAiLabel(false);
                                        setModalVisible(true);
                                    }}
                                >
                                    <MessageCircleQuestion className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                                    Add question
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setActiveField(null);
                                        setCreatingQuestion(false);
                                        setCreatingAiLabel(true);
                                        setModalVisible(true);
                                    }}
                                >
                                    <Sparkles className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                                    Add AI label
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Settings Section */}
            {isStandard && (
                <div className="overflow-hidden rounded-lg border bg-card">
                    <Collapsible
                        open={expandedSections['settings']}
                        onOpenChange={() => toggleSection('settings')}
                    >
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 transition-colors">
                                <div className="text-left">
                                    <div className="flex items-center gap-2">
                                        {expandedSections['settings'] ? (
                                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                        ) : (
                                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                        )}
                                        <h4 className="text-sm font-semibold">Collection settings</h4>
                                    </div>
                                    <p className="text-xs text-muted-foreground ml-6">
                                        Configure how and when data is collected
                                    </p>
                                </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <div className="border-t divide-y">
                                {/* Work Email Only */}
                                <div className="flex items-center justify-between gap-6 p-4">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-sm">Work email only</span>
                                                {!checkLicenseType('pro') && (
                                                    <Badge variant="outline" className="text-[10px] text-muted-foreground">
                                                        Pro
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Only accept professional email addresses
                                            </p>
                                        </div>
                                    <TooltipProvider>
                                        <Tooltip delayDuration={300}>
                                            <TooltipTrigger asChild>
                                                <div>
                                                    <Switch
                                                        checked={!!workEmailOnly}
                                                        onCheckedChange={setWorkEmailOnly as any}
                                                        disabled={!checkLicenseType('pro')}
                                                    />
                                                </div>
                                            </TooltipTrigger>
                                            {!checkLicenseType('pro') && (
                                                <TooltipContent>
                                                    <p>Upgrade to Engage to enable this feature</p>
                                                </TooltipContent>
                                            )}
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>

                                {/* Require Contact Info */}
                                <div className="flex items-center justify-between gap-6 p-4">
                                        <div>
                                            <span className="font-medium text-sm">Require contact info</span>
                                            <p className="text-xs text-muted-foreground">
                                                Force visitors to provide contact info after a set number of messages
                                            </p>
                                        </div>
                                    <Switch
                                        checked={!!requireContactInfo}
                                        onCheckedChange={(checked) => {
                                            (setRequireContactInfo as any)?.(checked);
                                        }}
                                    />
                                </div>

                                {/* Hide Contact Us Button */}
                                <div className="flex items-center justify-between gap-6 p-4">
                                        <div>
                                            <span className="font-medium text-sm">Hide “Contact Us” button</span>
                                            <p className="text-xs text-muted-foreground">
                                                Removes the inline widget button. Forced contact collection can still show the form.
                                            </p>
                                        </div>
                                    <Switch
                                        checked={!!hideContactUsButton}
                                        onCheckedChange={(checked) => {
                                            (setHideContactUsButton as any)?.(checked);
                                        }}
                                    />
                                </div>

                                {/* Messages Before Contact Info */}
                                {!!requireContactInfo && (
                                    <div className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <span className="font-medium text-sm">Messages before required</span>
                                                <p className="text-xs text-muted-foreground">
                                                    Set to 0 to require contact info immediately
                                                </p>
                                            </div>
                                            <Input
                                                type="number"
                                                min="0"
                                                max="50"
                                                value={messagesBeforeContactInfo as number}
                                                onChange={(e) => {
                                                    const value = Math.max(0, Math.min(50, parseInt(e.target.value) || 0));
                                                    (setMessagesBeforeContactInfo as any)?.(value);
                                                }}
                                                className="w-20 text-center"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                </div>
            )}
        </div>
    );
};

const SortableField = ({ field, toggleModal, deleteField, getTypeLabel }: {
    field: FormField; 
    toggleModal: (field?: FormField) => void;
    deleteField: (field: FormField) => void;
    getTypeLabel: (type: string) => string;
}) => {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: field.name });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition || 'transform 200ms cubic-bezier(0.25, 1, 0.5, 1)',
        zIndex: isDragging ? 50 : 0,
        opacity: isDragging ? 0.95 : 1,
    };

    const isQuestion = !!field.question;
    const isAiLabel = !!field.ai_label;
    const systemFieldNames = ['email', 'first_name', 'last_name', 'phone', 'company', 'job_title'];
    const isSystemField = systemFieldNames.includes(field.name);
    const itemKind = isAiLabel ? 'AI label' : isQuestion ? 'Question' : 'Field';
    const ItemIcon = isAiLabel ? Sparkles : isQuestion ? MessageCircleQuestion : Database;
    const metadata = [
        getTypeLabel(field.type),
        field.options?.length
            ? `${field.options.length} option${field.options.length === 1 ? '' : 's'}`
            : null,
        isSystemField ? 'System' : null,
        field.required ? 'Required' : null,
        field.type === 'tel' && field.requireCountryCode ? 'Country code required' : null,
    ].filter(Boolean);

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                'group/card rounded-md border bg-background transition-colors',
                isDragging
                    ? 'shadow-sm ring-1 ring-primary/30'
                    : 'hover:border-muted-foreground/30',
            )}
        >
            <div className="flex min-w-0 items-center gap-2 px-3 py-2.5">
                {/* Drag Handle */}
                <div
                    {...attributes}
                    {...listeners}
                    className="cursor-grab rounded p-1 text-muted-foreground/60 transition-colors hover:bg-muted hover:text-muted-foreground active:cursor-grabbing"
                >
                    <GripVertical className="h-4 w-4" />
                </div>

                <ItemIcon
                    className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                    strokeWidth={1.75}
                    aria-hidden="true"
                />

                {/* Field Info */}
                <button
                    type="button"
                    className="min-w-0 flex-1 cursor-pointer overflow-hidden py-0.5 text-left"
                    onClick={() => toggleModal(field)}
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-medium">{field.label}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{itemKind}</span>
                        {field.hidden && (
                            <Badge variant="outline" className="h-5 shrink-0 px-1.5 text-[10px] font-normal text-muted-foreground">
                                Hidden
                            </Badge>
                        )}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        <span className="font-mono">{field.name}</span>
                        {metadata.map((item) => (
                            <span key={item}> · {item}</span>
                        ))}
                    </div>
                </button>

                {/* Actions */}
                <div className="shrink-0">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground"
                                aria-label={`Actions for ${field.label}`}
                            >
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => toggleModal(field)}>
                                <Pencil className="mr-2 h-3.5 w-3.5" />
                                Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onSelect={() => setDeleteDialogOpen(true)}
                            >
                                <Trash2 className="mr-2 h-3.5 w-3.5" />
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Delete {field.ai_label ? 'AI Label' : field.question ? 'Question' : 'Field'}</DialogTitle>
                                <DialogDescription>
                                    Are you sure you want to delete "{field.label}"? This action cannot be undone. Any workflow or integration references to this field will be removed automatically.
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                                <DialogClose asChild>
                                    <Button variant="ghost">Cancel</Button>
                                </DialogClose>
                                <DialogClose asChild>
                                    <Button
                                        variant="destructive"
                                        onClick={() => {
                                            deleteField(field);
                                            setDeleteDialogOpen(false);
                                        }}
                                    >
                                        Delete
                                    </Button>
                                </DialogClose>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default Form;